import React, { useState } from 'react';
import { X, Heart, Gift, Users, Code, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import DonationButton from './DonationButton';

interface DonationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DonationModal: React.FC<DonationModalProps> = ({ isOpen, onClose }) => {
  const [selectedAmount, setSelectedAmount] = useState<string>('');

  const donationReasons = [
    {
      icon: Code,
      title: 'Support Development',
      description: 'Help us build new features and improve the platform',
      color: 'text-blue-500'
    },
    {
      icon: Zap,
      title: 'Server Costs',
      description: 'Keep TypeRush fast and reliable for everyone',
      color: 'text-yellow-500'
    },
    {
      icon: Users,
      title: 'Community Growth',
      description: 'Support events, competitions, and community initiatives',
      color: 'text-green-500'
    },
    {
      icon: Gift,
      title: 'Free for All',
      description: 'Keep core features free for everyone to enjoy',
      color: 'text-purple-500'
    }
  ];

  const predefinedAmounts = ['$5', '$10', '$25', '$50', '$100'];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          className="bg-cardBg rounded-xl border border-accent/20 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-accent/20 rounded-lg">
                <Heart className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-accent">Support TypeRush</h2>
                <p className="text-textMuted">Help us keep improving and growing</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-textMuted hover:text-text transition-colors p-1"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Why Donate Section */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-text">Why Support Us?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {donationReasons.map((reason, index) => {
                const Icon = reason.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-background/50 rounded-lg p-4 border border-accent/10"
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 mt-0.5 ${reason.color}`} />
                      <div>
                        <h4 className="font-semibold text-text mb-1">{reason.title}</h4>
                        <p className="text-sm text-textMuted">{reason.description}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Quick Amount Selection and Donation */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3 text-text">Quick Amounts</h3>
            <div className="flex flex-wrap items-center gap-2">
              {predefinedAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => setSelectedAmount(amount)}
                  className={`px-4 py-2 rounded-lg border transition-all ${
                    selectedAmount === amount
                      ? 'border-accent bg-accent/20 text-accent'
                      : 'border-accent/20 bg-background/50 text-textMuted hover:border-accent/40 hover:text-text'
                  }`}
                >
                  {amount}
                </button>
              ))}
              <div className="ml-4">
                <DonationButton 
                  variant="primary" 
                  className="px-8 py-3"
                  size="lg"
                />
              </div>
            </div>
            {selectedAmount && (
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-accent mt-2"
              >
                Selected: {selectedAmount} - Thank you for your generosity! 💙
              </motion.p>
            )}
          </div>

          {/* Crypto Info */}
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-accent mb-2">🔐 Secure Crypto Donations</h4>
            <p className="text-sm text-textMuted mb-2">
              We accept donations in various cryptocurrencies through Atlos.io - a secure and trusted payment gateway.
            </p>
            <ul className="text-sm text-textMuted space-y-1">
              <li>• No personal information required</li>
              <li>• Fast and secure transactions</li>
              <li>• Multiple cryptocurrency options</li>
              <li>• Instant confirmation</li>
            </ul>
          </div>

          {/* Thank You Message */}
          <div className="text-center">
            <p className="text-textMuted text-sm">
              Every donation, no matter how small, helps us keep TypeRush free and awesome for everyone. 
              <br />
              Thank you for being part of our community! ❤️
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DonationModal;
